import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../App';
import { getBatch, getSession, updateSession, updateSessionStatus, addSessionRecording, addSessionResources, removeSessionResource } from '../../services/lmsService';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import Footer from '../Footer';
import { ArrowLeft, Calendar, Clock, Video, PlayCircle, Users, CheckCircle, XCircle, AlertCircle, Link2, Plus, Trash2, ExternalLink, Settings, RefreshCw } from 'lucide-react';

const BatchSessionManager = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [sessionForm, setSessionForm] = useState({ title: '', description: '', meeting_link: '', meeting_password: '' });
  
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [recordingForm, setRecordingForm] = useState({ recording_url: '', recording_password: '', duration_minutes: 60 });
  
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [resourceForm, setResourceForm] = useState({ title: '', url: '', type: 'pdf' });
  
  useEffect(() => {
    // Allow both admin and sub_admin roles
    if (!user || (user.lms_role !== 'admin' && user.lms_role !== 'sub_admin')) { navigate('/'); return; }
    fetchBatch();
  }, [batchId, user]);
  
  const fetchBatch = async () => {
    try {
      setLoading(true);
      const response = await getBatch(batchId);
      if (response.success) setBatch(response.data);
    } catch (error) {
      toast.error('Failed to load batch');
      navigate('/lms-admin');
    } finally {
      setLoading(false);
    }
  };
  
  const openSessionDetail = async (session) => {
    try {
      const response = await getSession(session.id);
      if (response.success) {
        setSessionDetail(response.data);
        setSessionForm({ title: response.data.title || '', description: response.data.description || '', meeting_link: response.data.meeting_link || '', meeting_password: response.data.meeting_password || '' });
        setShowSessionModal(true);
      }
    } catch (error) { toast.error('Failed to load session'); }
  };
  
  const handleUpdateSession = async () => {
    try {
      await updateSession(sessionDetail.id, sessionForm);
      toast.success('Session updated');
      setShowSessionModal(false);
      fetchBatch();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update'); }
  };
  
  const handleStatusChange = async (sessionId, status) => {
    try {
      await updateSessionStatus(sessionId, status);
      toast.success(`Session ${status}`);
      fetchBatch();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };
  
  const handleAddRecording = async () => {
    try {
      await addSessionRecording(sessionDetail.id, recordingForm);
      toast.success('Recording added');
      setShowRecordingModal(false);
      setRecordingForm({ recording_url: '', recording_password: '', duration_minutes: 60 });
      const response = await getSession(sessionDetail.id);
      if (response.success) setSessionDetail(response.data);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };
  
  const handleAddResource = async () => {
    try {
      await addSessionResources(sessionDetail.id, [resourceForm]);
      toast.success('Resource added');
      setShowResourceModal(false);
      setResourceForm({ title: '', url: '', type: 'pdf' });
      const response = await getSession(sessionDetail.id);
      if (response.success) setSessionDetail(response.data);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };
  
  const handleRemoveResource = async (index) => {
    try {
      await removeSessionResource(sessionDetail.id, index);
      toast.success('Resource removed');
      const response = await getSession(sessionDetail.id);
      if (response.success) setSessionDetail(response.data);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };
  
  const getStatusBadge = (status) => {
    const badges = {
      scheduled: <Badge className="bg-blue-100 text-blue-700"><Clock className="w-3 h-3 mr-1" />Scheduled</Badge>,
      live: <Badge className="bg-red-100 text-red-700 animate-pulse"><Video className="w-3 h-3 mr-1" />Live</Badge>,
      completed: <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>,
      cancelled: <Badge className="bg-gray-100 text-gray-700"><XCircle className="w-3 h-3 mr-1" />Cancelled</Badge>,
      rescheduled: <Badge className="bg-yellow-100 text-yellow-700"><AlertCircle className="w-3 h-3 mr-1" />Rescheduled</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };
  
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading batch...</p>
        </div>
      </div>
    );
  }

  if (!batch) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-6xl mx-auto">
          <Button variant="ghost" className="mb-4" onClick={() => navigate('/lms-admin')}>
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Dashboard
          </Button>
          
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{batch.name}</h1>
            <p className="text-gray-600 text-sm">{batch.course_title}</p>
            <div className="flex flex-wrap gap-3 sm:gap-4 mt-2 text-xs sm:text-sm text-gray-500">
              <span><Calendar className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />{formatDate(batch.start_date)} - {formatDate(batch.end_date)}</span>
              <span><Users className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />{batch.current_enrolled}/{batch.max_students}</span>
              <span><Video className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />{batch.sessions?.length || 0} sessions</span>
            </div>
          </div>
          
          <h2 className="text-lg font-bold text-gray-900 mb-4 sm:mb-6">Session Schedule</h2>
          
          {batch.sessions?.length === 0 ? (
            <Card className="text-center py-8 sm:py-12">
              <CardContent>
                <Calendar className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-600">No sessions scheduled</h3>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {batch.sessions?.map((session, index) => (
                <Card key={session.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => openSessionDetail(session)}>
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex justify-between items-start mb-2 sm:mb-3">
                      <span className="text-xs sm:text-sm font-medium text-gray-500">Session {index + 1}</span>
                      {getStatusBadge(session.status)}
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base line-clamp-1">{session.title || `Session ${index + 1}`}</h3>
                    <div className="text-xs sm:text-sm text-gray-500 space-y-1">
                      <p><Calendar className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />{formatDate(session.scheduled_date)}</p>
                      <p><Clock className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />{session.scheduled_time} ({session.duration_minutes}m)</p>
                      {session.topic_title && <p><PlayCircle className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />{session.topic_title}</p>}
                    </div>
                    
                    <div className="flex gap-2 mt-3 sm:mt-4 pt-2 sm:pt-3 border-t">
                      {session.status === 'scheduled' && (
                        <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={(e) => { e.stopPropagation(); handleStatusChange(session.id, 'live'); }}>Start</Button>
                      )}
                      {session.status === 'live' && (
                        <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-xs" onClick={(e) => { e.stopPropagation(); handleStatusChange(session.id, 'completed'); }}>End</Button>
                      )}
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openSessionDetail(session); }}><Settings className="w-3 h-3 sm:w-4 sm:h-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Session Detail Modal */}
      <Dialog open={showSessionModal} onOpenChange={setShowSessionModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Session Details</DialogTitle></DialogHeader>
          
          {sessionDetail && (
            <div className="space-y-6 py-4">
              <div className="space-y-4">
                <div className="space-y-2"><Label>Title</Label><Input value={sessionForm.title} onChange={(e) => setSessionForm({ ...sessionForm, title: e.target.value })} placeholder="Session title" /></div>
                <div className="space-y-2"><Label>Description</Label><Textarea value={sessionForm.description} onChange={(e) => setSessionForm({ ...sessionForm, description: e.target.value })} rows={2} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Meeting Link</Label><Input value={sessionForm.meeting_link} onChange={(e) => setSessionForm({ ...sessionForm, meeting_link: e.target.value })} placeholder="https://..." /></div>
                  <div className="space-y-2"><Label>Password</Label><Input value={sessionForm.meeting_password} onChange={(e) => setSessionForm({ ...sessionForm, meeting_password: e.target.value })} /></div>
                </div>
                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleUpdateSession}>Save Changes</Button>
              </div>
              
              {/* Recording */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold">Recording</h4>
                  {!sessionDetail.recording && <Button size="sm" variant="outline" onClick={() => setShowRecordingModal(true)}><Plus className="w-4 h-4 mr-1" />Add</Button>}
                </div>
                {sessionDetail.recording ? (
                  <div className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                    <div><p className="font-medium text-sm">Recording Available</p><p className="text-xs text-gray-500">{sessionDetail.recording.duration_minutes}m</p></div>
                    <Button size="sm" variant="outline" onClick={() => window.open(sessionDetail.recording.recording_url, '_blank')}><ExternalLink className="w-4 h-4 mr-1" />View</Button>
                  </div>
                ) : <p className="text-gray-500 text-sm">No recording</p>}
              </div>
              
              {/* Resources */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold">Resources</h4>
                  <Button size="sm" variant="outline" onClick={() => setShowResourceModal(true)}><Plus className="w-4 h-4 mr-1" />Add</Button>
                </div>
                {sessionDetail.resources?.length > 0 ? (
                  <div className="space-y-2">
                    {sessionDetail.resources.map((resource, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <div className="flex items-center gap-2"><Link2 className="w-4 h-4 text-gray-400" /><span>{resource.title}</span><Badge variant="outline" className="text-xs">{resource.type}</Badge></div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => window.open(resource.url, '_blank')}><ExternalLink className="w-4 h-4" /></Button>
                          {user.lms_role === 'admin' && (
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleRemoveResource(i)}><Trash2 className="w-4 h-4" /></Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-gray-500 text-sm">No resources</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Recording Modal */}
      <Dialog open={showRecordingModal} onOpenChange={setShowRecordingModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Recording</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>URL *</Label><Input value={recordingForm.recording_url} onChange={(e) => setRecordingForm({ ...recordingForm, recording_url: e.target.value })} placeholder="https://..." /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Password</Label><Input value={recordingForm.recording_password} onChange={(e) => setRecordingForm({ ...recordingForm, recording_password: e.target.value })} /></div>
              <div className="space-y-2"><Label>Duration (min)</Label><Input type="number" value={recordingForm.duration_minutes} onChange={(e) => setRecordingForm({ ...recordingForm, duration_minutes: parseInt(e.target.value) })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecordingModal(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleAddRecording} disabled={!recordingForm.recording_url}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Resource Modal */}
      <Dialog open={showResourceModal} onOpenChange={setShowResourceModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Resource</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Title *</Label><Input value={resourceForm.title} onChange={(e) => setResourceForm({ ...resourceForm, title: e.target.value })} /></div>
            <div className="space-y-2"><Label>URL *</Label><Input value={resourceForm.url} onChange={(e) => setResourceForm({ ...resourceForm, url: e.target.value })} placeholder="https://..." /></div>
            <div className="space-y-2"><Label>Type</Label><Select value={resourceForm.type} onValueChange={(v) => setResourceForm({ ...resourceForm, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="pdf">PDF</SelectItem><SelectItem value="doc">Document</SelectItem><SelectItem value="video">Video</SelectItem><SelectItem value="link">Link</SelectItem><SelectItem value="code">Code</SelectItem></SelectContent></Select></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResourceModal(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleAddResource} disabled={!resourceForm.title || !resourceForm.url}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
};

export default BatchSessionManager;
